/**
 * Domain-agnostic SDK wrapper for the folders-with-guidelines endpoint.
 *
 * Lifted from `features/guidelines/api/folders.api.ts` so any surface that
 * needs the workspace-scoped folders+guidelines list (the guidelines feature
 * itself, the chats `ChatGuidelinesSelector`, etc.) can consume it without
 * crossing into the `guidelines` feature slice.
 *
 * The original feature path re-exports this symbol for backward compatibility
 * — existing consumers keep working unchanged.
 */
import {
  FoldersService,
  type WorkspaceResolutionResponseDto,
} from '@inference.engine/guidelines-api-client';
import { queryOptions } from '@tanstack/react-query';
import { FolderWithGuidelinesDto, guidelinesHttp } from '@/lib/api/guidelines-client';
import { SUPPRESS_ERROR_NOTIFICATION_HEADER } from '@/lib/api/interceptors/problem-details.interceptor';

/**
 * Fetches all folders with their guidelines for a given workspace.
 * @param workspaceId - The workspace ID to fetch folders for
 * @returns Array of FolderWithGuidelinesDto objects
 */
export const getFoldersWithGuidelines = async (
  workspaceId: string,
): Promise<FolderWithGuidelinesDto[]> => {
  const response = await FoldersService.getFoldersWithGuidelines({
    client: guidelinesHttp,
    query: { workspaceId },
  });
  return response.data ?? [];
};

export const resolveFolderWorkspace = async (
  folderId: string,
): Promise<WorkspaceResolutionResponseDto> => {
  const response = await FoldersService.resolveWorkspace({
    client: guidelinesHttp,
    path: { id: folderId },
    // The resolution seam maps a 404 to its own generic "not available"
    // state; the global toast would leak the raw error first.
    // eslint-disable-next-line i18next/no-literal-string -- request header value, never rendered
    headers: { [SUPPRESS_ERROR_NOTIFICATION_HEADER]: 'true' },
  });

  if (!response.data) {
    throw new Error('Failed to resolve guidelines folder');
  }

  return response.data;
};

/**
 * Deep-link resolution of a folder id to its owning workspace. Runs BEFORE the
 * workspace-scoped folder/guidelines fetches (via `useDeepLinkResolution`) so a
 * deep link from another workspace doesn't resolve against the ambient
 * `x-selected-workspace-id` header and silently miss the folder.
 */
export const folderResolutionQuery = (folderId?: string) =>
  queryOptions({
    queryKey: ['folder', folderId ?? '', 'resolution'] as const,
    queryFn: () => resolveFolderWorkspace(folderId!),
  });
